/**
 * App Mode Service (Singleton)
 *
 * Handles detection of embedded vs standalone mode and manages
 * secure token communication via postMessage API.
 *
 * Features:
 * - Auto-detects iframe embedding
 * - Listens for secure token via postMessage
 * - Manages token state and validation
 * - Provides subscription pattern for React components
 */

class AppModeService {
  constructor() {
    // Private state
    this.mode = null;
    this.token = null;
    this.companyKey = null;
    this.isTokenReady = false;
    this.error = null;
    this.listeners = new Set(); // React components listening for updates
    this.tokenTimeout = null;
    this.messageHandler = null;
    this.TOKEN_TIMEOUT_MS = 10000; // 10 seconds

    // Initialize immediately
    this.detectMode();
  }

  /**
   * Detect if portal is running in embedded mode (iframe) or standalone
   */
  detectMode() {
    const isInIframe = window.self !== window.top;

    this.mode = isInIframe ? 'embedded' : 'standalone';
    this.notifyListeners();

    // If embedded, set up postMessage listener
    if (isInIframe) {
      this.setupPostMessageListener();
      this.requestToken();
      this.startTokenTimeout();
    }
  }

  /**
   * Set up postMessage listener to receive token from parent
   */
  setupPostMessageListener() {
    const ALLOWED_ORIGINS = [import.meta.env.REACT_APP_HEALTH_BENEFITS_PORTAL].filter(Boolean);

    this.messageHandler = event => {
      // Security: validate origin
      if (ALLOWED_ORIGINS.length === 0) {
        this.setError('Security configuration error: No allowed origins configured.');
        return;
      }

      if (!ALLOWED_ORIGINS.includes(event.origin)) {
        return;
      }

      // Handle message from trusted origin
      this.handlePostMessage(event.data, event.origin);
    };

    window.addEventListener('message', this.messageHandler);
  }

  /**
   * Handle incoming postMessage data
   * @param {Object} data - The message data
   * @param {string} origin - The origin of the message (for logging)
   */
  handlePostMessage(data) {
    if (!data || typeof data !== 'object') {
      return;
    }

    switch (data.type) {
      case 'PORTAL_INIT':
      case 'AUTH_TOKEN':
        this.setToken(data.token);
        if (data.companyKey) {
          this.setCompanyKey(data.companyKey);
        }
        this.clearTokenTimeout();
        break;

      case 'TOKEN_UPDATE':
        this.setToken(data.token);
        break;

      case 'TOKEN_ERROR':
        this.setError(data.message || 'Failed to receive token');
        this.clearTokenTimeout();
        break;

      default:
        break;
    }
  }

  /**
   * Set the authentication token
   */
  setToken(token) {
    if (!token || typeof token !== 'string') {
      this.setError('Invalid token format');
      return;
    }

    this.token = token;
    this.isTokenReady = true;
    this.error = null;
    this.notifyListeners();
  }

  /**
   * Set the company key
   */
  setCompanyKey(companyKey) {
    this.companyKey = companyKey;
    this.notifyListeners();
  }

  /**
   * Get the current authentication token
   */
  getToken() {
    return this.token;
  }

  /**
   * Get the current company key
   */
  getCompanyKey() {
    return this.companyKey;
  }

  /**
   * Clear the token
   */
  clearToken() {
    this.token = null;
    this.companyKey = null;
    this.isTokenReady = false;
    this.notifyListeners();
  }

  /**
   * Set error state
   */
  setError(errorMessage) {
    this.error = errorMessage;
    this.isTokenReady = false;
    this.notifyListeners();
  }

  /**
   * Get parent origin for secure postMessage communication
   * @returns {string|null} The parent origin or null if not available
   */
  // eslint-disable-next-line class-methods-use-this
  getParentOrigin() {
    // This method doesn't use 'this' but is an instance method for consistency
    // Try to get parent origin from referrer
    if (document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer);
        return referrerUrl.origin;
      } catch (error) {
        // Failed to parse referrer, continue to fallback
      }
    }

    // Fallback to environment variable
    const parentOrigin = import.meta.env.REACT_APP_HEALTH_BENEFITS_PORTAL;
    if (parentOrigin) {
      return parentOrigin;
    }

    return null;
  }

  /**
   * Request token from parent portal
   */
  requestToken() {
    if (window.parent && window.parent !== window.self) {
      const parentOrigin = this.getParentOrigin();

      if (!parentOrigin || parentOrigin === 'null') {
        this.setError('Security error: Cannot determine parent origin.');
        return;
      }

      // Use specific origin instead of wildcard for security
      window.parent.postMessage({ type: 'REQUEST_TOKEN' }, parentOrigin);
    }
  }

  /**
   * Start timeout for token reception
   */
  startTokenTimeout() {
    this.clearTokenTimeout();

    this.tokenTimeout = setTimeout(() => {
      if (!this.isTokenReady) {
        this.setError('Token request timed out. Please refresh the page.');
      }
    }, this.TOKEN_TIMEOUT_MS);
  }

  /**
   * Clear token timeout
   */
  clearTokenTimeout() {
    if (this.tokenTimeout) {
      clearTimeout(this.tokenTimeout);
      this.tokenTimeout = null;
    }
  }

  /**
   * Subscribe to state changes (for React components)
   * @param {Function} listener - Callback function to call when state changes
   * @returns {Function} Unsubscribe function
   */
  subscribe(listener) {
    this.listeners.add(listener);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of state changes
   */
  notifyListeners() {
    this.listeners.forEach(listener => {
      listener();
    });
  }

  /**
   * Get current state snapshot
   */
  getState() {
    return {
      mode: this.mode,
      token: this.token,
      companyKey: this.companyKey,
      isTokenReady: this.isTokenReady,
      isEmbedded: this.isEmbedded,
      isStandalone: this.isStandalone,
      error: this.error,
    };
  }

  /**
   * Check if running in embedded mode
   */
  get isEmbedded() {
    return this.mode === 'embedded';
  }

  /**
   * Check if running in standalone mode
   */
  get isStandalone() {
    return this.mode === 'standalone';
  }

  /**
   * Cleanup method (for testing or cleanup)
   */
  destroy() {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
    this.clearTokenTimeout();
    this.listeners.clear();
  }
}

// Create singleton instance
const appModeService = new AppModeService();

export default appModeService;
